import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { Record1098E } from "./interface/1098-E-interface";
import {
  generateRandomRecordId,
  generateUniqueTransmissionId,
  generateUniqueSubmissionId,
} from "../../constants/genRecordId";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { parseAmount } from "../../constants/validationFunctions";

// Validation function for state codes
//TODO: REMOVE AFTER DATA RESET
function validateStateCode(state: string | undefined): string {
  const validStates = [
    "AL",
    "AK",
    "AS",
    "AZ",
    "AR",
    "CA",
    "CO",
    "MP",
    "CT",
    "DE",
    "DC",
    "FM",
    "FL",
    "GA",
    "GU",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MH",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PW",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "VI",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
    "AA",
    "AE",
    "AP",
  ];

  if (!state) return "AA"; // Default to 'AA' if no state provided

  const upperState = state.trim().toUpperCase().substring(0, 2);

  // If the state is valid, return it
  if (validStates.includes(upperState)) {
    return upperState;
  }

  // If invalid, return the default 'AA'
  return "AA";
}

export async function generate1098EXml(
  ctx: EngineContext,
  records: Record1098E[],
  queryParams?: any,
) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    return {
      validationResponse: [
        {
          error: true,
          message: "No records found or records input is invalid.",
        },
      ],
      xml: null,
      taxYear: null,
    };
  }

  // Filter records by fileType if specified in query parameters
  let filteredRecords = records;
  if (queryParams && queryParams.fileType) {
    const targetFileType = queryParams.fileType.toUpperCase();

    filteredRecords = records.filter((record) => {
      return (
        record["1098_YEAR"] &&
        (targetFileType === "1098-E" || targetFileType === "1098E")
      );
    });

    if (filteredRecords.length === 0) {
      return {
        validationResponse: [
          {
            error: true,
            message: `No records found with fileType: ${queryParams.fileType}`,
          },
        ],
        xml: null,
        taxYear: null,
      };
    }
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: false,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Get tax year from records or use default
  const taxYear = resolveIrsIntakeTaxYear(filteredRecords[0], ctx.irs.maxIntakeTaxYear);

  const form1098EDetails = filteredRecords.map((record) => {
    // Safely handle string operations
    const safePersonNameControl = record.RECIPIENT_NAME
      ? (record.RECIPIENT_NAME.substring(0, 4) || "").toUpperCase()
      : "";

    const safeLastName = record.RECIPIENT_NAME2 || "UNKNOWN";
    const safeCity = record.RECIPIENT_CITY || "UNKNOWN";

    // Create the form details with correct element ordering
    const formDetails: any = {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear.toString(),
      RecordId: record.id.toString(),
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",

      // RecipientDetail must come first according to schema
      // TY2026 layout-V2 records file via the shared split builder (business
      // as BusinessName+BUSINESS_TIN); legacy records keep the shape below.
      RecipientDetail: buildSplitRecipientDetail(
        record,
        transmitterData.testFileIndicator === "T"
          ? "000000000"
          : (record.RECIPIENT_ID_NUMBER || "000000000").replace(/-/g, ""),
        {
          USAddress: {
            // AddressLine1Txt is required
            AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: safeCity,
            StateAbbreviationCd: validateStateCode(record.RECIPIENT_STATE),
            ZIPCd: record.RECIPIENT_ZIP || "00000",
          },
        },
      ) ?? {
        TIN:
          transmitterData.testFileIndicator === "T"
            ? "000000000"
            : (record.RECIPIENT_ID_NUMBER || "000000000").replace(/-/g, ""),
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        ...(safePersonNameControl && {
          PersonNameControlTxt: safePersonNameControl,
        }),
        PersonName: {
          // IMPORTANT: PersonFirstNm must come before PersonLastNm
          PersonFirstNm: record.RECIPIENT_NAME || "UNKNOWN",
          PersonLastNm: safeLastName,
        },
        MailingAddressGrp: {
          USAddress: {
            // AddressLine1Txt is required
            AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN ADDRESS",
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: safeCity,
            StateAbbreviationCd: validateStateCode(record.RECIPIENT_STATE),
            ZIPCd: record.RECIPIENT_ZIP || "00000",
          },
        },
      },

      // Optional "PrevSubmittedRecRecipientGrp" for corrections
      ...(record.corrected && {
        PrevSubmittedRecRecipientGrp: {
          PrevSubmittedTIN: (record.RECIPIENT_ID_NUMBER || "000000000").replace(
            /-/g,
            "",
          ),
          PrevSubmittedName: record.RECIPIENT_NAME,
        },
      }),

      // RecipientAccountNum comes before StudentLoanIntRcvdByLenderAmt and other fields
      ...(record.ACCOUNT_NUMBER && {
        RecipientAccountNum: record.ACCOUNT_NUMBER,
      }),

      // StudentLoanIntRcvdByLenderAmt is required for 1098-E
      StudentLoanIntRcvdByLenderAmt: parseAmount(
        record.BOX1_STUDENT_LOAN_INTEREST || "0",
      ),

      // Optional fields
      ...(record.BOX2_LOAN_ORIGINATION_FEES_IND && {
        LoanOriginationFeeInd: "1",
      }),

      // Required field: NotLoanOrigFeeOrCapInterestInd - this is required by the IRS schema
      NotLoanOrigFeeOrCapInterestInd: record.BOX2_LOAN_ORIGINATION_FEES_IND
        ? "0"
        : "1",
    };

    return formDetails;
  });

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
        "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear), // NEW for TY2025 - must come before UniqueTransmissionId
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form1098EDetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1098E",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: "2023-04-04",
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: form1098EDetails.length.toString(),
            IRSubmission1FormTotals: {
              Form1098ETotalAmtGrp: {
                StudentLoanIntRcvdByLenderAmt: filteredRecords
                  .reduce(
                    (sum, r) =>
                      sum +
                      parseFloat(
                        parseAmount(r.BOX1_STUDENT_LOAN_INTEREST || "0"),
                      ),
                    0,
                  )
                  .toFixed(2),
              },
            },
          },
          IRSubmission1Detail: {
            Form1098EDetail: form1098EDetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return {
      xml,
      taxYear,
      validationResponse: [], // Empty array means no validation errors
    };
  } catch (error) {
    console.error("Error generating XML:", error);
    return {
      validationResponse: [
        {
          error: true,
          message: "Failed to generate XML",
          details: error instanceof Error ? error.message : String(error),
        },
      ],
      xml: null,
      taxYear,
    };
  }
}
